import { TFunction } from "i18next";
import { Link } from "react-router-dom";

import { EventTypeCellParts, EventTypeStrategy } from "./EventTypeStrategy";
import { getTranslatedEventTypeName } from "../../utils/eventTypeUtils";

import { Button } from "@/components/ui/v2/button";
import { TransactionType } from "@/types/transaction/TransactionType";

export class CampaignBudgetAllocatedEventTypeStrategy
implements EventTypeStrategy
{
  renderCell(row: TransactionType, t: TFunction): JSX.Element {
    const forText = row.eventData.campaignName ? (
      <>
        {" "}
        {t("for")} <b>{row.eventData.campaignName}</b>
      </>
    ) : (
      ""
    );
    const onText = row.eventData.jobBoardName ? (
      <>
        {" "}
        {t("on")} <b>{row.eventData.jobBoardName}</b>
      </>
    ) : (
      ""
    );
    const inText = row.eventData.month ? (
      <>
        {" "}
        {t("in")}{" "}
        <b>
          {new Date(row.eventData.month).toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          })}
        </b>
      </>
    ) : (
      ""
    );

    return (
      <>
        {t(`eventTypes.${row.eventType}`)}
        {forText}
        {onText}
        {inText}
      </>
    );
  }

  getTypeAndDescription(
    row: TransactionType,
    t: TFunction
  ): EventTypeCellParts {
    // Safely access eventData with fallback
    const eventData = row.eventData || {};
    const campaignName = eventData.campaignName;
    const jobBoardName = eventData.jobBoardName;
    const campaignId = eventData.campaignId;
    const clientIntegrationModuleId =
      eventData.clientIntegrationModuleId ?? eventData.integrationModuleId ?? eventData.jobBoardId;
    const clientId = eventData.clientId || row.clientId2;
    const month = eventData.month;

    // Build description parts
    let campaignPart: JSX.Element | null = null;
    let jobBoardPart: JSX.Element | null = null;
    let monthPart: JSX.Element | null = null;

    // "for [Campaign Name (link)]"
    if (
      campaignName &&
      typeof campaignName === "string" &&
      campaignName.trim()
    ) {
      if (campaignId && clientId) {
        campaignPart = (
          <>
            {t("common.for")}{" "}
            <Button asChild variant={"link"} className="px-0">
              <Link to={`/clients/${clientId}/campaigns/${campaignId}`}>
                {campaignName}
              </Link>
            </Button>
          </>
        );
      } else {
        campaignPart = (
          <>
            {t("common.for")} <b>{campaignName}</b>
          </>
        );
      }
    } else if (campaignId) {
      if (clientId) {
        campaignPart = (
          <>
            {t("common.for")}{" "}
            <Button asChild variant={"link"} className="px-0">
              <Link to={`/clients/${clientId}/campaigns/${campaignId}`}>
                {campaignId}
              </Link>
            </Button>
          </>
        );
      } else {
        campaignPart = (
          <>
            {t("common.for")} <b>{campaignId}</b>
          </>
        );
      }
    }

    // "on [Job Board Name (link)]"
    if (
      jobBoardName &&
      typeof jobBoardName === "string" &&
      jobBoardName.trim()
    ) {
      if (clientIntegrationModuleId && campaignId && clientId) {
        jobBoardPart = (
          <>
            {" "}
            {t("common.on")}{" "}
            <Button asChild variant={"link"} className="px-0">
              <Link
                to={`/clients/${clientId}/campaigns/${campaignId}/integrations/${clientIntegrationModuleId}`}
              >
                {jobBoardName}
              </Link>
            </Button>
          </>
        );
      } else {
        jobBoardPart = (
          <>
            {campaignPart ? " " : ""}
            {t("common.on")} <b>{jobBoardName}</b>
          </>
        );
      }
    } else if (clientIntegrationModuleId) {
      if (campaignId && clientId) {
        jobBoardPart = (
          <>
            {" "}
            {t("common.on")}{" "}
            <Button asChild variant={"link"} className="px-0">
              <Link
                to={`/clients/${clientId}/campaigns/${campaignId}/integrations/${clientIntegrationModuleId}`}
              >
                {clientIntegrationModuleId}
              </Link>
            </Button>
          </>
        );
      } else {
        jobBoardPart = (
          <>
            {campaignPart ? " " : ""}
            {t("common.on")} <b>{clientIntegrationModuleId}</b>
          </>
        );
      }
    }

    // "in [Month Year]"
    if (month) {
      try {
        const monthDate = new Date(month);
        if (!Number.isNaN(monthDate.getTime())) {
          monthPart = (
            <>
              {" "}
              {t("common.in")}{" "}
              <b>
                {monthDate.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </b>
            </>
          );
        }
      } catch {
        // Invalid date, skip
      }
    }

    return {
      type: <>{getTranslatedEventTypeName(row.eventType, t)}</>,
      description: (
        <>
          {campaignPart}
          {jobBoardPart}
          {monthPart}
        </>
      ),
    };
  }
}
